import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { LmxTour } from './LmxTour';
import { useLmxStore } from './lmxStore';
import { useWorkflowStore } from '../store/workflowStore';
import useTimelineStore from '../store/timelineStore';
import { EVT_CLOSED, openLearning } from '../components/learning/learningModel';

const API = process.env.REACT_APP_BACKEND_URL;
const FIRST_DELAY_MS = 900;

const lsDoneKey = (userId) => `lmx.tour.done.${userId || 'anon'}`;
const lsDone = (userId) => {
  try { return localStorage.getItem(lsDoneKey(userId)) === '1'; } catch { return false; }
};
const lsMark = (userId) => {
  try { localStorage.setItem(lsDoneKey(userId), '1'); } catch { /* privado */ }
};

/**
 * LmxTourGate — decide CUÁNDO abrir el recorrido y le pasa las props.
 *
 *  · primera vez: cuando el dock terminó su entrada (mode deja de ser
 *    'curtain') + 900 ms, si el usuario no está en medio de algo.
 *  · una vez por usuario: lmx_user_prefs.prefs.tour_done (con localStorage
 *    de respaldo mientras o si falla).
 *  · reapertura manual (aunque ya se haya visto): evento global
 *    `window.dispatchEvent(new CustomEvent('lmx:tour', { detail: { startAt } }))`
 *    — lo disparan el botón "?", el chip "Recorrido" del dock y Shift + ?.
 */
export const LmxTourGate = () => {
  const { getToken, userId } = useAuth();
  const mode = useLmxStore((s) => s.mode);
  const assets = useLmxStore((s) => s.assets);
  const prefs = useLmxStore((s) => s.prefs);
  const [open, setOpen] = useState(false);
  const [startAt, setStartAt] = useState(undefined);
  const [retry, setRetry] = useState(0);
  const triedRef = useRef(false);

  const markDone = useCallback(async () => {
    lsMark(userId);
    try {
      const token = await getToken();
      await fetch(`${API}/api/lmx/me/prefs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prefs: { tour_done: true } }),
      });
    } catch { /* queda el respaldo local */ }
  }, [getToken, userId]);

  const openTour = useCallback((at) => {
    setStartAt(at || undefined);
    setOpen(true);
  }, []);

  // ¿Está en medio de algo? Ante la duda, no interrumpir.
  const isBusyRightNow = useCallback(() => {
    if (useTimelineStore.getState().isOpen) return true;
    const wf = useWorkflowStore.getState();
    if (wf.isRunningWorkflow) return true;
    if (Object.keys(wf.inFlight || {}).length > 0) return true;
    if (useLmxStore.getState().busy) return true;
    // Un modal abierto (guardado, confirmaciones, propiedades, etc.)
    if (document.querySelector('[role="dialog"], .node-busy-overlay')) return true;
    return false;
  }, []);

  // ── Apertura automática: sólo la primera vez ──
  useEffect(() => {
    if (triedRef.current) return undefined;
    if (mode === 'curtain') return undefined;  // todavía no aterrizó el orbe
    if (prefs?.prefs?.tour_done || lsDone(userId)) {
      triedRef.current = true;
      return undefined;
    }
    if (isBusyRightNow()) return undefined;    // espera al próximo ingreso
    triedRef.current = true;
    const t = setTimeout(() => {
      if (!isBusyRightNow()) openTour(undefined);
    }, FIRST_DELAY_MS);
    return () => clearTimeout(t);
  }, [mode, prefs, userId, openTour, isBusyRightNow, retry]);

  // Envío AP-1 · si los capítulos de aprendizaje estaban abiertos al iniciar,
  // el recorrido espera a que se cierren y recién ahí vuelve a probar.
  useEffect(() => {
    const onClosed = (e) => {
      if (e.detail?.mode !== 'onboarding') return;
      if (prefs?.prefs?.tour_done || lsDone(userId)) return;
      triedRef.current = false;
      setRetry((n) => n + 1);
    };
    window.addEventListener(EVT_CLOSED, onClosed);
    return () => window.removeEventListener(EVT_CLOSED, onClosed);
  }, [prefs, userId]);

  // ── Reapertura manual (evento global) + Shift + ? ──
  useEffect(() => {
    const onEvent = (e) => openTour(e.detail?.startAt);
    const onKey = (e) => {
      if (!(e.shiftKey && (e.key === '?' || e.key === '¿'))) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
          || el.isContentEditable)) return;
      e.preventDefault();
      openTour(undefined);
    };
    window.addEventListener('lmx:tour', onEvent);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('lmx:tour', onEvent);
      window.removeEventListener('keydown', onKey);
    };
  }, [openTour]);

  return (
    <LmxTour
      open={open}
      startAt={startAt}
      orb={assets?.orb}
      onStart={() => {
        useTimelineStore.setState({ isOpen: false });
        useWorkflowStore.getState().setSelectedNode?.(null);
        useLmxStore.getState().collapse?.();
      }}
      onClose={(reason) => {
        setOpen(false);
        markDone();
        if (reason === 'learning') setTimeout(() => openLearning(), 150);
      }}
      onCta={() => {
        useLmxStore.getState().open();
        setTimeout(() => document.querySelector('[data-testid="lmx-dock-input"]')?.focus(), 250);
      }}
    />
  );
};

export default LmxTourGate;
